/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useCallback, useState } from 'react';

import { IconCollapse, IconExpand } from '@tock/icons';
import { Theme, css } from '@emotion/react';
import { disableBackgroundScroll, resumeBackgroundScroll } from 'utils/scrollUtils';
import { useMountEffect } from '@tock/hooks';
import ControlButton from 'components/profile/culinary-travel-log/ControlButton';
import ControlPanel from 'components/profile/culinary-travel-log/ControlPanel';

export interface Props {
  element: HTMLElement;
}

const controlsStyles = ({ breakpoints }: Theme) => css`
  ${breakpoints.down('sm')} {
    display: none;
  }
`;

const FullscreenControl: FunctionComponent<Props> = ({ element }) => {
  // isFullScreen does not cause a component rerender, so using state keeps the icons in sync
  const [isMapFullScreen, setIsMapFullScreen] = useState(isFullscreen());

  const fullScreenChangeHandler = useCallback(() => {
    const isFS = isFullscreen();
    setIsMapFullScreen(isFS);
    if (!isFS) {
      resumeBackgroundScroll(document);
    }
  }, []);

  useMountEffect(() => {
    document.addEventListener('fullscreenchange', fullScreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', fullScreenChangeHandler);

    return () => {
      document.removeEventListener('fullscreenchange', fullScreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', fullScreenChangeHandler);
    };
  });

  return (
    <ControlPanel css={controlsStyles}>
      <ControlButton
        onClick={() => {
          if (isMapFullScreen) {
            exitFullscreen();
          } else {
            enterFullscreen(element);
          }
        }}
        title="Fullscreen"
      >
        {isMapFullScreen ? <IconCollapse /> : <IconExpand />}
      </ControlButton>
    </ControlPanel>
  );
};

export default FullscreenControl;

function isFullscreen() {
  if (document.fullscreenElement !== undefined) {
    return document.fullscreenElement !== null;
  } else {
    const d = document as any;
    if (d.webkitFullscreenElement !== undefined) {
      return d.webkitFullscreenElement !== null;
    } else {
      return false;
    }
  }
}

function enterFullscreen(element: HTMLElement) {
  disableBackgroundScroll(document);
  if (element.requestFullscreen) {
    return element.requestFullscreen();
  } else {
    // For older browsers there are several vendor-prefixed functions to try.
    const e = element as any;
    return new Promise((resolve, reject) => {
      if (e.webkitRequestFullscreen) {
        resolve(e.webkitRequestFullscreen());
      } else if (e.mozRequestFullScreen) {
        resolve(e.mozRequestFullScreen());
      } else if (e.msRequestFullscreen) {
        resolve(e.msRequestFullscreen());
      }
    });
  }
}

// re-enabling background scroll has to be handled separately from this function
// for the case of exiting through the escape key
function exitFullscreen() {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  } else {
    // For older browsers there are several vendor-prefixed functions to try.
    const d = document as any;
    return new Promise((resolve, reject) => {
      if (d.webkitExitFullscreen) {
        resolve(d.webkitExitFullscreen());
      } else if (d.mozCancelFullScreen) {
        resolve(d.mozCancelFullScreen());
      } else if (d.msExitFullscreen) {
        resolve(d.msExitFullscreen());
      }
    });
  }
}
